import React, { useEffect, useMemo, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { ChatInput } from "./ChatInput";
import { useExtractChat } from "../../hooks/useExtractChat";

interface ExtractChatPanelProps {
  jobId: string;
  fileName: string;
  extractedText: string;
  onStartOver?: () => void;
}

const formatWordCount = (text: string): string => {
  const wordCount = text.trim() ? text.trim().split(/\s+/u).length : 0;
  return `${wordCount.toLocaleString()} words`;
};

const ExtractChatPanel: React.FC<ExtractChatPanelProps> = ({
  jobId,
  fileName,
  extractedText,
  onStartOver,
}) => {
  const { messages, isLoading, error, sendMessage, clearMessages } = useExtractChat({ jobId, extractedText });
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isLoading]);

  const wordCountLabel = useMemo(() => formatWordCount(extractedText), [extractedText]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minHeight: 560,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            icon={<DescriptionOutlinedIcon />}
            label={fileName}
            sx={{
              maxWidth: "100%",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
          <Chip label={wordCountLabel} variant="outlined" />
          <Chip label="Extraction loaded into chat context" variant="outlined" />
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {messages.length > 0 && (
            <Button
              variant="outlined"
              onClick={clearMessages}
              disabled={isLoading}
              startIcon={<DeleteOutlineRoundedIcon />}
            >
              Clear Chat
            </Button>
          )}
          {onStartOver && (
            <Button
              variant="outlined"
              onClick={onStartOver}
              disabled={isLoading}
              startIcon={<AutorenewRoundedIcon />}
            >
              Upload Another
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: "4px",
          border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(9, 18, 28, 0.82)",
          backdropFilter: "blur(18px)",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--teal)",
              fontSize: "0.82rem",
            }}
          >
            Document Chat
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.72)", mt: 0.75, fontSize: "0.92rem" }}>
            Ask questions about the extracted content. Answers are grounded in the uploaded document.
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2.5,
            py: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {messages.length === 0 && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 3,
              }}
            >
              <Box sx={{ maxWidth: 560 }}>
                <SmartToyOutlinedIcon sx={{ fontSize: 40, color: "var(--teal)", mb: 1.5 }} />
                <Typography sx={{ color: "#fff", fontSize: "1.05rem", fontWeight: 600, mb: 1 }}>
                  The document is ready for chat.
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.7 }}>
                  Try asking for key clauses, definitions, obligations, or whether a specific phrase appears in the extracted text.
                </Typography>
              </Box>
            </Box>
          )}

          {messages.map((message) => {
            const isUser = message.role === "user";
            return (
              <Box
                key={message.id}
                sx={{
                  display: "flex",
                  justifyContent: isUser ? "flex-end" : "flex-start",
                }}
              >
                <Box
                  sx={{
                    maxWidth: { xs: "100%", md: "85%" },
                    px: 2,
                    py: 1.5,
                    borderRadius: 3,
                    bgcolor: isUser ? "rgba(44, 192, 183, 0.18)" : "rgba(255,255,255,0.07)",
                    border: isUser
                      ? "1px solid rgba(44, 192, 183, 0.35)"
                      : "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <Typography
                    sx={{
                      color: isUser ? "#dffdfa" : "#f7fbff",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.7,
                      fontSize: "0.95rem",
                    }}
                  >
                    {message.content}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: "0.72rem",
                      color: "rgba(255,255,255,0.5)",
                    }}
                  >
                    {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </Typography>
                </Box>
              </Box>
            );
          })}

          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  color: "#f7fbff",
                }}
              >
                <CircularProgress size={16} thickness={5} sx={{ color: "var(--teal)" }} />
                <Typography sx={{ fontSize: "0.92rem" }}>Answering from extracted content...</Typography>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            bgcolor: "rgba(255,255,255,0.02)",
          }}
        >
          <ChatInput
            onSend={sendMessage}
            disabled={isLoading}
            placeholder="Ask a question about this document..."
            maxLength={2000}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ExtractChatPanel;
